import React, { Component } from "react";
import { Row, Col, Card, Button, Table, Spinner } from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList } from "./CrmApi";

export default class CrmDashboard extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      counts: {
        contacts: 0,
        leads: 0,
        appointments: 0,
        cases: 0,
        activities: 0,
        invoices: 0,
        roles: 0,
        users: 0,
        servicePlans: 0,
        tags: 0,
      },
    };
  }

  componentDidMount() {
    this.loadCounts();
  }

  loadCounts = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "Missing StoreId." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const [
        contacts,
        leads,
        appointments,
        cases,
        activities,
        invoices,
        roles,
        users,
        servicePlans,
        tags,
      ] = await Promise.all([
        fetchCrmList("Contacts", "GetContacts", storeId),
        fetchCrmList("Leads", "GetLeads", storeId),
        fetchCrmList("Appointments", "GetAppointments", storeId),
        fetchCrmList("Cases", "GetCases", storeId),
        fetchCrmList("Activities", "GetActivities", storeId),
        fetchCrmList("Invoices", "GetInvoices", storeId),
        fetchCrmList("Roles", "GetRoles", storeId),
        fetchCrmList("Users", "GetUsers", storeId),
        fetchCrmList("ServicePlans", "GetServicePlans", storeId),
        fetchCrmList("Tags", "GetTags", storeId),
      ]);

      this.setState({
        loading: false,
        counts: {
          contacts: Array.isArray(contacts) ? contacts.length : 0,
          leads: Array.isArray(leads) ? leads.length : 0,
          appointments: Array.isArray(appointments) ? appointments.length : 0,
          cases: Array.isArray(cases) ? cases.length : 0,
          activities: Array.isArray(activities) ? activities.length : 0,
          invoices: Array.isArray(invoices) ? invoices.length : 0,
          roles: Array.isArray(roles) ? roles.length : 0,
          users: Array.isArray(users) ? users.length : 0,
          servicePlans: Array.isArray(servicePlans) ? servicePlans.length : 0,
          tags: Array.isArray(tags) ? tags.length : 0,
        },
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load CRM counts.",
      });
    }
  };

  render() {
    const { loading, error, counts } = this.state;
    const storeId = this.context.storeId;

    if (!storeId) {
      return (
        <div className="m-3">
          <h2>CRM Dashboard</h2>
          <div className="alert alert-warning">
            StoreId is not available. Please select a store first.
          </div>
        </div>
      );
    }

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Dashboard</h2>
            <p className="text-muted">
              Quick overview of CRM data for the selected store.
            </p>
          </Col>
          <Col xs="auto">
            <Button
              variant="secondary"
              onClick={this.loadCounts}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {error && <div className="alert alert-danger">{error}</div>}

        <Row xs={1} md={3} className="g-3 mb-4">
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Contacts</Card.Title>
                <Card.Text className="display-6">{counts.contacts}</Card.Text>
                <Button href="/CrmContacts" variant="primary">
                  View contacts
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Leads</Card.Title>
                <Card.Text className="display-6">{counts.leads}</Card.Text>
                <Button href="/CrmLeads" variant="primary">
                  View leads
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Appointments</Card.Title>
                <Card.Text className="display-6">
                  {counts.appointments}
                </Card.Text>
                <Button href="/CrmAppointments" variant="primary">
                  View appointments
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row xs={1} md={3} className="g-3">
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Cases</Card.Title>
                <Card.Text className="display-6">{counts.cases}</Card.Text>
                <Button href="/CrmCases" variant="primary">
                  View cases
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Activities</Card.Title>
                <Card.Text className="display-6">{counts.activities}</Card.Text>
                <Button href="/CrmActivities" variant="primary">
                  View activities
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Invoices</Card.Title>
                <Card.Text className="display-6">{counts.invoices}</Card.Text>
                <Button href="/CrmInvoices" variant="primary">
                  View invoices
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row xs={1} md={3} className="g-3 mt-3">
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Roles</Card.Title>
                <Card.Text className="display-6">{counts.roles}</Card.Text>
                <Button href="/CrmRoles" variant="primary">
                  View roles
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Users</Card.Title>
                <Card.Text className="display-6">{counts.users}</Card.Text>
                <Button href="/CrmUsers" variant="primary">
                  View users
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Service Plans</Card.Title>
                <Card.Text className="display-6">
                  {counts.servicePlans}
                </Card.Text>
                <Button href="/CrmServicePlans" variant="primary">
                  View service plans
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row xs={1} md={3} className="g-3 mt-3">
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Tags</Card.Title>
                <Card.Text className="display-6">{counts.tags}</Card.Text>
                <Button href="/CrmTags" variant="primary">
                  View tags
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="mt-4">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Module</th>
                <th>Count</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Contacts</td>
                <td>{counts.contacts}</td>
                <td>
                  <Button size="sm" href="/CrmContacts">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Leads</td>
                <td>{counts.leads}</td>
                <td>
                  <Button size="sm" href="/CrmLeads">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Appointments</td>
                <td>{counts.appointments}</td>
                <td>
                  <Button size="sm" href="/CrmAppointments">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Cases</td>
                <td>{counts.cases}</td>
                <td>
                  <Button size="sm" href="/CrmCases">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Activities</td>
                <td>{counts.activities}</td>
                <td>
                  <Button size="sm" href="/CrmActivities">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Invoices</td>
                <td>{counts.invoices}</td>
                <td>
                  <Button size="sm" href="/CrmInvoices">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Roles</td>
                <td>{counts.roles}</td>
                <td>
                  <Button size="sm" href="/CrmRoles">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Users</td>
                <td>{counts.users}</td>
                <td>
                  <Button size="sm" href="/CrmUsers">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Service Plans</td>
                <td>{counts.servicePlans}</td>
                <td>
                  <Button size="sm" href="/CrmServicePlans">
                    Open
                  </Button>
                </td>
              </tr>
              <tr>
                <td>Tags</td>
                <td>{counts.tags}</td>
                <td>
                  <Button size="sm" href="/CrmTags">
                    Open
                  </Button>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>
    );
  }
}
